'use client';

import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getUsersService } from '@/services/User';
import { getToken } from '@/services/_utils';
import { SUBSIDIARY_USERS_QUERY_KEY } from '@/modules/salesReport/SalesReport.constants';
import {
  paletteForIndex,
  paletteForSeed,
} from '@/components/chat/ContactAvatar';

type Palette = { bg: string; fg: string };

interface ResponsibleColorContextType {
  colorFor: (id?: string | number | null) => Palette;
}

const ResponsibleColorContext = createContext<
  ResponsibleColorContextType | undefined
>(undefined);

// Antes cada ResponsibleAvatar (una por fila de ChatList/Kanban/mensaje) traía
// su propio useQuery(['subsidiary','users']) + su propio useMemo de
// ordenamiento -- con 152 chats visibles eran 152 observers y 152 sorts
// redundantes del mismo listado de usuarios. Este provider corre esa cuenta
// una sola vez y la comparte por contexto.
export const useResponsibleColor = () => {
  const context = useContext(ResponsibleColorContext);
  if (context === undefined) {
    throw new Error(
      'useResponsibleColor must be used within a ResponsibleColorProvider'
    );
  }
  return context;
};

export const ResponsibleColorProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { data } = useQuery({
    queryKey: SUBSIDIARY_USERS_QUERY_KEY,
    queryFn: ({ signal }) => getUsersService({ signal, limit: 100 }),
    enabled: !!getToken(),
  });

  const indexById = useMemo(() => {
    const users = data?.results ?? [];
    const sorted = [...users].sort((a, b) =>
      String(a.id).localeCompare(String(b.id), undefined, { numeric: true })
    );
    return new Map(sorted.map((u, i) => [String(u.id), i] as const));
  }, [data]);

  const value = useMemo(() => {
    const colorFor = (id?: string | number | null): Palette => {
      const key = id != null ? String(id) : '';
      const index = indexById.get(key);
      return index !== undefined
        ? paletteForIndex(index)
        : paletteForSeed(key || 'anon');
    };
    return { colorFor };
  }, [indexById]);

  return (
    <ResponsibleColorContext.Provider value={value}>
      {children}
    </ResponsibleColorContext.Provider>
  );
};
